import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';

/**
 * Header actions component that manages cart notifications.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} liveRegion - The live region for cart announcements.
 *
 * @extends {Component<Refs>}
 */
class HeaderActions extends Component {
  requiredRefs = ['liveRegion'];

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate);
    this.addEventListener('submit', this.#onLanguageSubmit);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate);
    this.removeEventListener('submit', this.#onLanguageSubmit);
  }

  /**
   * Keep the selected pill in place while Shopify switches language.
   * @param {SubmitEvent} event
   */
  #onLanguageSubmit = (event) => {
    const form = event.target;
    const button = event.submitter;
    if (!(form instanceof HTMLFormElement) || !form.matches('.header-language-switcher__form')) return;
    if (!(button instanceof HTMLButtonElement) || button.name !== 'language_code') return;

    form.dataset.languageSubmitting = 'true';
    form.querySelectorAll('.header-language-switcher__option').forEach((option) => {
      option.removeAttribute('aria-current');
    });
    button.setAttribute('aria-current', 'true');
  };

  /**
   * Handles cart update events and announces the new count to screen readers.
   * @param {CustomEvent<{ resource?: { item_count?: number } }>} event
   */
  #onCartUpdate = (event) => {
    const cartCount = event.detail.resource?.item_count;
    if (cartCount === undefined) return;

    this.refs.liveRegion.textContent = `${Theme.translations.cart_count}: ${cartCount}`;
  };
}

if (!customElements.get('header-actions')) {
  customElements.define('header-actions', HeaderActions);
}
